import React, { useState } from 'react';

// webpush-client-gaps: the in-room opt-in for offline web push.
//
// Why this exists at all: `ensureSubscribed` used to call
// `Notification.requestPermission()` straight from a mount effect. Chrome
// tolerates that; SAFARI REQUIRES A USER GESTURE and silently declines
// otherwise — so on Safari the whole feature was invisible (no prompt, no
// error, no log). This bar is that gesture.
//
// It's also better on Chrome. A permission prompt fired on page load with no
// explanation is exactly the pattern browsers have trained people to dismiss,
// and a dismissed prompt is unrecoverable in-page — `denied` is sticky and can
// only be undone in browser settings. So the ask is explained, and it only
// happens when the user opts in.
//
// Shown ONLY when permission is still 'default' (nobody has been asked) in a
// push-enabled room while logged in. Never shown for 'granted' (already on),
// 'denied' (can't recover here — nagging is pointless), or 'unsupported'.
const PushOptIn = ({ onEnable, onDismiss }) => {
  const [busy, setBusy] = useState(false);

  const enable = async () => {
    setBusy(true);
    try {
      await onEnable();
    } finally {
      // The parent unmounts us on success (permission is no longer 'default').
      // On refusal we stay mounted but re-enabled — except the browser won't
      // ask twice, so this mainly covers the transient-failure case.
      setBusy(false);
    }
  };

  return (
    <div
      role="region"
      aria-label="Offline notifications"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        flexWrap: 'wrap',
        margin: '8px 0',
        padding: '10px 14px',
        borderRadius: '10px',
        background: '#f4f6f8',
        border: '1px solid #e2e6ea',
        fontSize: '0.9rem',
      }}
    >
      <span style={{ flex: '1 1 220px', color: '#3c4650' }}>
        Get notified when someone posts here while you're away.
      </span>
      <button
        type="button"
        onClick={enable}
        disabled={busy}
        style={{
          padding: '6px 14px',
          borderRadius: '8px',
          border: 'none',
          background: busy ? '#9bb0c4' : '#2f6fb0',
          color: '#ffffff',
          cursor: busy ? 'default' : 'pointer',
          fontSize: '0.9rem',
        }}
      >
        {busy ? 'Enabling…' : 'Notify me'}
      </button>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss notification prompt"
        style={{
          padding: '6px 10px',
          borderRadius: '8px',
          border: 'none',
          background: 'transparent',
          color: '#6b7680',
          cursor: 'pointer',
          fontSize: '0.9rem',
        }}
      >
        Not now
      </button>
    </div>
  );
};

export default PushOptIn;
